// Birim varyantı DR7 — önizleme adımının birim bölümü: sayaç + süzgeç, uyumsuz satır öğesi ve
// "Müşteri için gösterge birimini değiştir" penceresi (yalnız iç sihirbaz). Portal aynı etiketleri
// görür, eylemleri görmez (onUnitChange/onSkipRow verilmez).

import { useEffect, useState } from "react";
import type { KpiSetIndicator } from "@mahir/shared";
import { getKpiSet, listKpiMetrics } from "../../../api/kpi";
import { KPI_IMPORT_COPY } from "../../../lib/kpi-import-copy";
import type { UnitStateCounts } from "../../../lib/kpi-import-wizard";
import { useKpiStore } from "../../../stores/kpi-store";
import {
  IndicatorUnitSection,
  type CatalogUnitInfo,
  type IndicatorUnitApplied,
} from "../IndicatorUnitSection";
import { KpiModal } from "../KpiModal";
import { btnSmall } from "../kpi-form-styles";
import { IncompatibleLabel } from "./ImportUnitStateLabel";

const U = KPI_IMPORT_COPY.unit;

export type UnitFilter = "all" | "converted" | "incompatible" | "unknown";

/** Sayaç + süzgeç; üç sayı da 0 ise hiç render edilmez. */
export function UnitCounterBar(x: {
  counts: UnitStateCounts;
  filter: UnitFilter;
  onFilter: (f: UnitFilter) => void;
}): JSX.Element | null {
  const { converted, incompatible, unknown } = x.counts;
  if (converted + incompatible + unknown === 0) return null;
  const items: [UnitFilter, number][] = [
    ["converted", converted],
    ["incompatible", incompatible],
    ["unknown", unknown],
  ];
  return (
    <div
      data-testid="kpi-import-unit-counter"
      className="flex items-center gap-2 flex-wrap"
      style={{ fontSize: "0.875rem" }}
    >
      <span style={{ color: "var(--color-text-2)" }}>
        {U.counter(converted, incompatible, unknown)}
      </span>
      {items.map(([f, n]) =>
        n > 0 ? (
          <button
            key={f}
            type="button"
            aria-pressed={x.filter === f}
            style={{
              ...btnSmall,
              fontSize: "0.875rem",
              ...(x.filter === f ? { borderColor: "var(--color-primary)", fontWeight: 600 } : {}),
            }}
            onClick={() => x.onFilter(x.filter === f ? "all" : f)}
          >
            {f === "converted"
              ? KPI_IMPORT_COPY.preview.convertedTag
              : f === "incompatible"
                ? U.incompatible.split(" · ")[0]
                : U.unknownTag}{" "}
            ({n.toString()})
          </button>
        ) : null,
      )}
    </div>
  );
}

/** Atlanan satır listesindeki uyumsuz-birim öğesi: etiket + (iç sihirbazda) iki eylem. */
export function IncompatibleRowItem(x: {
  rowLabel: string;
  file: string;
  target: string;
  onUnitChange?: (() => void) | undefined;
  onSkipRow?: (() => void) | undefined;
}): JSX.Element {
  return (
    <div data-testid="kpi-import-unit-row">
      <b>{x.rowLabel}</b>
      <div style={{ marginTop: 2 }}>
        <IncompatibleLabel file={x.file} target={x.target} />
      </div>
      {x.onUnitChange || x.onSkipRow ? (
        <div className="flex gap-2 flex-wrap" style={{ marginTop: 4 }}>
          {x.onUnitChange ? (
            <button
              type="button"
              style={{ ...btnSmall, fontSize: "0.875rem" }}
              onClick={x.onUnitChange}
            >
              {U.changeForClient}
            </button>
          ) : null}
          {x.onSkipRow ? (
            <button
              type="button"
              style={{ ...btnSmall, fontSize: "0.875rem" }}
              onClick={x.onSkipRow}
            >
              {U.skipRow}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/** Katalog metriğinin varsayılan birimi + alternatifleri (bulunamazsa null). */
export function catalogUnitFor(
  metrics: readonly {
    id: string;
    defaultUnit: string | null;
    unitVariants?: string[] | undefined;
  }[],
  metricId: string,
): CatalogUnitInfo | null {
  const m = metrics.find((x) => x.id === metricId);
  return m ? { defaultUnit: m.defaultUnit, unitVariants: m.unitVariants ?? [] } : null;
}

/** Pencere gövdesi (saf): katalog birimi seçiciye aynen verilir. */
export function UnitChangeDialogBody(x: {
  indicator: KpiSetIndicator;
  clientId: string;
  clientName: string;
  catalogUnit: CatalogUnitInfo | null;
  onApplied: (r: IndicatorUnitApplied) => void;
}): JSX.Element {
  return (
    <IndicatorUnitSection
      indicator={x.indicator}
      clientId={x.clientId}
      clientName={x.clientName}
      catalogUnit={x.catalogUnit}
      disabled={false}
      onApplied={x.onApplied}
    />
  );
}

/** Hedef göstergenin müşteri birimini önizlemeden çıkmadan değiştirir (IndicatorUnitSection). */
export function UnitChangeDialog(x: {
  setId: string;
  clientId: string;
  targetCode: string;
  onApplied: (r: IndicatorUnitApplied) => void;
  onClose: () => void;
}): JSX.Element {
  const clientName = useKpiStore((s) => s.clients.find((c) => c.id === x.clientId)?.name ?? "");
  const [ind, setInd] = useState<KpiSetIndicator | null>(null);
  const [catalogUnit, setCatalogUnit] = useState<CatalogUnitInfo | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    Promise.all([getKpiSet(x.setId), listKpiMetrics({ clientId: x.clientId })])
      .then(([r, cat]) => {
        const found = r.indicators.find((i) => i.code === x.targetCode) ?? null;
        if (alive) {
          if (found) {
            setCatalogUnit(catalogUnitFor(cat.metrics, found.metricId));
            setInd(found);
          } else setFailed(true);
        }
      })
      .catch(() => {
        if (alive) setFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [x.setId, x.clientId, x.targetCode]);
  return (
    <KpiModal open title={U.modalTitle} onClose={x.onClose}>
      {failed ? (
        <div role="alert">{U.modalError}</div>
      ) : !ind ? (
        <div role="status">{U.modalLoading}</div>
      ) : (
        <UnitChangeDialogBody
          indicator={ind}
          clientId={x.clientId}
          clientName={clientName}
          catalogUnit={catalogUnit}
          onApplied={(r) => {
            x.onApplied(r);
            x.onClose();
          }}
        />
      )}
    </KpiModal>
  );
}
